import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useShiftsChannel } from '../../lib/api/realtime.js';
import { useLiveTenant } from '../live/useLiveState.js';
import { api } from '../../lib/api/client.js';
import { Badge, Empty, ErrorBanner, Segmented, Stat, StatGroup, TableSkeleton }
  from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import type { AgentAttendance, AgentPauseReason, DispositionTotals } from '@arcnet/contracts';

/**
 * Attendance: who was logged on, for how long, and what the time went on (API agentshifts.ts).
 *
 * The question a supervisor actually asks is "was the day worked", so the row leads with hours
 * logged on and the share of them that counted as work, and the pauses are broken out beside it
 * rather than hidden in a total. A pause the account calls work -- training, coaching -- counts
 * as work here, because that is the account's decision to make and it is made once, on the
 * reason, rather than every time somebody reads a report.
 */

const DAY = 86_400_000;

/** SAST, like everything else people read here: a day runs midnight to midnight where they are. */
const OFFSET = '+02:00';
const dayStart = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T00:00:00${OFFSET}`;

type Period = 'today' | 'yesterday' | 'week' | 'month';

const PERIODS: ReadonlyArray<readonly [Period, string]> = [
  ['today', 'Today'], ['yesterday', 'Yesterday'], ['week', 'Last 7 days'], ['month', 'Last 30 days'],
];

function range(period: Period): { from: string; to: string } {
  const now = new Date();
  const today = dayStart(now);
  if (period === 'today') return { from: today, to: dayStart(new Date(now.getTime() + DAY)) };
  if (period === 'yesterday') {
    return { from: dayStart(new Date(now.getTime() - DAY)), to: today };
  }
  const days = period === 'week' ? 7 : 30;
  return { from: dayStart(new Date(now.getTime() - days * DAY)), to: dayStart(new Date(now.getTime() + DAY)) };
}

/** Hours and minutes, never seconds: nobody reads a shift to the second. */
const hm = (sec: number): string => {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};

/** A share of the whole, for a sub-line: "62%" and nothing when there is no whole. */
const pct = (n: number, total: number): string | undefined =>
  (total > 0 ? `${Math.round((n / total) * 100)}%` : undefined);

const clock = (iso: string | null): string => (iso
  ? new Date(iso).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: false })
  : '—');

export function AttendancePage() {
  const tenantId = useLiveTenant();
  const [period, setPeriod] = useState<Period>('today');
  const { from, to } = useMemo(() => range(period), [period]);

  const q = useQuery({
    queryKey: ['agent-attendance', tenantId ?? '', from, to],
    queryFn: () => api.get<{ data: AgentAttendance[]; reasons: AgentPauseReason[]; dispositions: DispositionTotals }>(
      `/v1/tenants/${tenantId}/agent-attendance?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
    enabled: !!tenantId,
    /**
     * A minute's poll as the floor, not the mechanism: the channel below refetches the moment
     * anything changes, and this catches a screen whose socket is down -- and keeps the clock
     * moving on somebody who is still logged on, whose time counts up without any event at all.
     */
    refetchInterval: period === 'today' ? 60_000 : false,
  });

  /**
   * Somebody logged on, paused or logged off: read it back at once rather than waiting out the
   * poll. The nudge carries nothing, so attendance still has exactly one way to be computed.
   */
  const live = useShiftsChannel(period === 'today' ? tenantId : null, () => { void q.refetch(); });

  if (!tenantId) {
    return <Empty icon="accounts" title="No account selected" hint="Choose an account to see its attendance." />;
  }

  const rows = q.data?.data ?? [];
  const marks = q.data?.dispositions;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Agent activity</h1>
          <p>Who was logged on, what the time went on, and what the calls were</p>
        </div>
      </div>

      {q.error && <ErrorBanner error={q.error} />}

      {/* What the calls were, over the same period. First, because "how did the day go" is
          answered by outcomes and only then by hours (owner, 24 September 2026). */}
      {marks && marks.total > 0 && (
        <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
          <StatGroup>
            <Stat bare label="Calls marked" value={String(marks.total)} />
            <Stat bare label="Positive" value={String(marks.positive)} tone="ok"
              sub={pct(marks.positive, marks.total)} />
            <Stat bare label="Neutral" value={String(marks.neutral)}
              sub={pct(marks.neutral, marks.total)} />
            <Stat bare label="Negative" value={String(marks.negative)} tone="warn"
              sub={pct(marks.negative, marks.total)} />
          </StatGroup>
          <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
            {marks.by_code.map((d) => (
              <span key={d.code} className="arc-pause-chip" data-kind={d.kind}>
                {d.label} {d.calls}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="arc-card">
        <div className="arc-toolbar">
          <Segmented label="Period" value={period} onChange={setPeriod}
            options={PERIODS.map(([k, label]) => [k, label] as const)} />
          <div className="arc-spacer" />
          {period === 'today' && live === 'live' && (
            <Badge tone="ok"><span className="arc-dot" aria-hidden="true" />live</Badge>
          )}
          {rows.length > 0 && (
            <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
              {rows.length} {rows.length === 1 ? 'person' : 'people'}
            </span>
          )}
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            <colgroup>
              <col />
              <col style={{ width: 96 }} />
              <col style={{ width: 96 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 96 }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: 120 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Extension</th>
                <th>On shift</th>
                <th>On calls</th>
                <th>Productive</th>
                <th>Marked</th>
                <th>Paused</th>
                <th>Pauses</th>
                <th>First / last</th>
              </tr>
            </thead>
            {q.data === undefined ? <TableSkeleton cols={8} /> : (
              <tbody>
                {rows.map((a) => (
                  <tr key={a.extension_id}>
                    <td>
                      <div className="arc-row" style={{ gap: 10 }}>
                        <span className="arc-ext-num">{a.extension_number}</span>
                        <div className="arc-stack" style={{ gap: 1 }}>
                          <span style={{ fontWeight: 550 }}>{a.display_name}</span>
                          <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                            {a.shifts} {a.shifts === 1 ? 'shift' : 'shifts'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="arc-num">{hm(a.logged_on_sec)}</td>
                    <td className="arc-num arc-muted">{hm(a.on_call_sec)}</td>
                    <td>
                      <div className="arc-stack" style={{ gap: 2 }}>
                        <span className="arc-num" style={{ fontWeight: 550 }}>
                          {a.productive_pct === null ? '—' : `${a.productive_pct}%`}
                        </span>
                        {/* The bar is the same number said again, for scanning a column of
                            people rather than reading each one. */}
                        <span className="arc-meter" aria-hidden="true">
                          <span style={{ width: `${Math.min(100, a.productive_pct ?? 0)}%` }} />
                        </span>
                      </div>
                    </td>
                    <td>
                      {/* How many of their calls were marked, and how they went. The codes
                          themselves are on the call; here it is the shape of the day. */}
                      {a.marked === 0 ? <span className="arc-subtle">—</span> : (
                        <div className="arc-stack" style={{ gap: 2 }}>
                          <span className="arc-num" style={{ fontWeight: 550 }}>{a.marked}</span>
                          <span className="arc-subtle" style={{ fontSize: 11 }}
                            title={a.by_disposition.map((d) => `${d.label} ${d.calls}`).join(', ')}>
                            {a.marked_positive > 0 && <span style={{ color: 'var(--arc-ok)' }}>{a.marked_positive} good</span>}
                            {a.marked_positive > 0 && a.marked_negative > 0 && ' · '}
                            {a.marked_negative > 0 && <span style={{ color: 'var(--arc-danger)' }}>{a.marked_negative} bad</span>}
                            {a.marked_positive === 0 && a.marked_negative === 0 && 'neutral'}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="arc-num arc-muted">{hm(a.paused_sec)}</td>
                    <td className="arc-muted">
                      {a.by_reason.length === 0 ? <span className="arc-subtle">—</span> : (
                        <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                          {a.by_reason.map((r) => (
                            <span key={r.reason} className="arc-pause-chip" data-productive={r.productive || undefined}
                              title={r.productive ? 'Counts as work' : 'Does not count as work'}>
                              {r.label} {hm(r.seconds)}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="arc-muted arc-num">
                      {/* Still on shift is a fact, not a missing value: an em dash where the
                          logout should be reads as data we lost. */}
                      {clock(a.first_login)} – {a.last_logout ? clock(a.last_logout)
                        : <span className="arc-subtle" style={{ fontSize: 11.5 }}>on shift</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {q.data !== undefined && rows.length === 0 && (
          <Empty icon="clock" title="Nobody was logged on"
            hint="Attendance starts when somebody logs on in the workspace." />
        )}
      </div>

      <p className="arc-hint" style={{ marginTop: 10 }}>
        <Icon name="info" size={12} /> Productive is time ready, on a call, wrapping up, and pauses
        this account counts as work. Which pauses those are is set on the reason itself.
      </p>
    </>
  );
}
